import React, { useState, useEffect } from 'react';
import { categoriesAPI, transportAPI } from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import Toast from './Toast';
import './addUser.css';

const AddCategory = ({ onBack, onCancel, onNavigate, userRole = 'admin' }) => {
  const [formData, setFormData] = useState({
    main: '',
    sub: '',
    common: '',
    city: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmState, setConfirmState] = useState({ open: false, message: '', onConfirm: null });
  const [cities, setCities] = useState([]);
  const [existingMain, setExistingMain] = useState([]);
  const [existingSub, setExistingSub] = useState([]);
  const [existingCommon, setExistingCommon] = useState([]);

  // Fetch cities and existing category values for dropdowns
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await transportAPI.getCities('');
        if (response.success) {
          setCities(response.cities || []);
        }
      } catch (err) {
        console.error('Error fetching cities:', err);
      }
    };
    const fetchExistingCategories = async () => {
      try {
        const response = await categoriesAPI.getAll();
        if (response && response.success) {
          const cats = response.categories || [];
          setExistingMain([...new Set(cats.map(c => c.main).filter(Boolean))].sort());
          setExistingSub([...new Set(cats.map(c => c.sub).filter(Boolean))].sort());
          setExistingCommon([...new Set(cats.map(c => c.common).filter(Boolean))].sort());
        }
      } catch (err) {
        console.error('Error fetching existing categories:', err);
      }
    };
    fetchCities();
    fetchExistingCategories();
  }, []);

  const handleBack = () => {
    if (onNavigate) {
      onNavigate('categoryMaster');
    } else if (onBack) {
      onBack();
    }
  };

  const handleHome = () => {
    if (onNavigate) {
      const backPath = userRole === 'admin' ? 'dashboard' : userRole === 'supervisor' ? 'supervisorHome' : 'staffHome';
      onNavigate(backPath);
    }
  };

  const handleManagers = () => {
    if (onNavigate && userRole === 'admin') {
      onNavigate('users');
    }
  };

  const handleProducts = () => {
    if (onNavigate) {
      onNavigate('products');
    }
  };

  const handleStaff = () => {
    if (onNavigate) {
      onNavigate('staff');
    }
  };

  const handleCustomers = () => {
    if (onNavigate) {
      onNavigate('customers');
    }
  };

  const handleSettings = () => {
    if (onNavigate) {
      onNavigate('settings');
    }
  };

  const handleCancel = () => {
    if (onNavigate) {
      onNavigate('categoryMaster');
    } else if (onCancel) {
      onCancel();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const submitCategory = async () => {
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await categoriesAPI.create({
        main: formData.main,
        sub: formData.sub,
        common: formData.common,
        city: formData.city
      });
      
      if (response && response.success) {
        setSuccessMessage('Category created successfully');
        setTimeout(() => {
          setSuccessMessage('');
          handleCancel();
        }, 2000);
      } else {
        setError(response?.error || 'Failed to create category. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Failed to create category. Please try again.');
      console.error('Create category error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Allow submission with any combination of fields
    setConfirmState({
      open: true,
      message: 'Are you sure you want to create this category?',
      onConfirm: submitCategory,
    });
  };

  return (
    <div className="dashboard-container">
      {/* Left Sidebar Navigation */}
      <nav className="sidebar-nav">
        <div className="nav-item" onClick={handleHome}>
          <div className="nav-icon">
            <i className="fas fa-home"></i>
          </div>
          <span>Home</span>
        </div>
        {userRole === 'admin' && (
          <div className="nav-item" onClick={handleManagers}>
            <div className="nav-icon">
              <i className="fas fa-users"></i>
            </div>
            <span>Supervisors</span>
          </div>
        )}
        <div className="nav-item" onClick={handleStaff}>
          <div className="nav-icon">
            <i className="fas fa-user-tie"></i>
          </div>
          <span>Staff</span>
        </div>
        <div className="nav-item active" onClick={() => onNavigate && onNavigate('masterMenu')}>
          <div className="nav-icon">
            <i className="fas fa-th-large"></i>
          </div>
          <span>Master Menu</span>
        </div>
        <div className="nav-item" onClick={() => onNavigate && onNavigate('transactionMenu')}>
          <div className="nav-icon">
            <i className="fas fa-exchange-alt"></i>
          </div>
          <span>Transaction</span>
        </div>
        <div className="nav-item" onClick={handleSettings}>
          <div className="nav-icon">
            <i className="fas fa-cog"></i>
          </div>
          <span>Settings</span>
        </div>
      </nav>

      {/* Main Content Area */}
      <div className="dashboard-main">
        <div className="add-user-container">
          {/* Header */}
          <header className="add-user-header">
            <button className="back-btn" onClick={handleBack}>
              <i className="fas fa-arrow-left"></i>
            </button>
            <div className="header-content">
              <h1 className="page-title">Add Category</h1>
            </div>
          </header>

          {/* Main Content */}
          <main className="add-user-content">
            <form onSubmit={handleSubmit} className="add-user-form" noValidate>
                {/* Category Fields - No section title */}
                <div className="form-section">
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="main">Main Category</label>
                      <div className="input-wrapper">
                        <i className="fas fa-tag input-icon"></i>
                        <input
                          type="text"
                          id="main"
                          name="main"
                          className="form-input"
                          placeholder="e.g., Utensils"
                          value={formData.main}
                          onChange={handleInputChange}
                          list="add-main-list"
                          autoComplete="off"
                        />
                        <datalist id="add-main-list">
                          {existingMain.map((val, i) => <option key={i} value={val} />)}
                        </datalist>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="sub">Sub Category</label>
                      <div className="input-wrapper">
                        <i className="fas fa-tags input-icon"></i>
                        <input
                          type="text"
                          id="sub"
                          name="sub"
                          className="form-input"
                          placeholder="e.g., Kitchen"
                          value={formData.sub}
                          onChange={handleInputChange}
                          list="add-sub-list"
                          autoComplete="off"
                        />
                        <datalist id="add-sub-list">
                          {existingSub.map((val, i) => <option key={i} value={val} />)}
                        </datalist>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="common">Common Category</label>
                      <div className="input-wrapper">
                        <i className="fas fa-list input-icon"></i>
                        <input
                          type="text"
                          id="common"
                          name="common"
                          className="form-input"
                          placeholder="e.g., Daily Use"
                          value={formData.common}
                          onChange={handleInputChange}
                          list="add-common-list"
                          autoComplete="off"
                        />
                        <datalist id="add-common-list">
                          {existingCommon.map((val, i) => <option key={i} value={val} />)}
                        </datalist>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="city">City</label>
                      <div className="input-wrapper">
                        <i className="fas fa-city input-icon"></i>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          className="form-input"
                          placeholder="Enter or select city"
                          value={formData.city}
                          onChange={handleInputChange}
                          list="cities-list"
                        />
                        <datalist id="cities-list">
                          {cities.map((city, index) => (
                            <option key={index} value={city} />
                          ))}
                        </datalist>
                      </div>
                    </div>
                  </div>
                </div>

                <Toast message={error} type="error" onClose={() => setError('')} />
                <Toast message={successMessage} type="success" onClose={() => setSuccessMessage('')} />

                {/* Action Buttons */}
                <div className="form-actions">
                  <button type="submit" className="create-user-btn" disabled={isLoading}>
                    {isLoading ? 'Creating...' : 'Add Category'}
                  </button>
                  <button type="button" className="cancel-btn" onClick={handleCancel}>
                    Cancel
                  </button>
                </div>
              </form>
          </main>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmState.open}
        title="Confirm Category Creation"
        message={confirmState.message}
        confirmText="Yes, Create"
        cancelText="Cancel"
        onConfirm={() => {
          setConfirmState({ open: false, message: '', onConfirm: null });
          if (confirmState.onConfirm) confirmState.onConfirm();
        }}
        onCancel={() => setConfirmState({ open: false, message: '', onConfirm: null })}
      />
    </div>
  );
};

export default AddCategory;

